import { Fragment, useRef, useState } from 'react';
import { MdClose, MdExpandMore } from 'react-icons/md';
import { chiaveGiorno, etichettaGiorno, ora } from '../utils/giorno';
import { toast } from 'react-toastify';
import { fetchOlderMessages } from '../store/messageSlice';
import { Avatar, Reactions, QuotedMessage, MediaMessage } from './ChatParts';
import { TestoConLink, AnteprimaLink } from './TestoMessaggio';
import BigliettiContatto from './BigliettoContatto';
import MessageMenu from './MessageMenu';
import MediaViewer, { apribile } from './MediaViewer';

const PRESSIONE_LUNGA_MS = 450;
const SCORRI_PER_RISPONDERE = 60;
const CHIAVE_SUGGERIMENTO = 'what:suggerimento-azioni-messaggio';

function suggerimentoVisto() {
  try { return localStorage.getItem(CHIAVE_SUGGERIMENTO) === '1'; } catch (_) { return true; }
}
function segnaSuggerimentoVisto() {
  try { localStorage.setItem(CHIAVE_SUGGERIMENTO, '1'); } catch (_) { /* senza memoria locale si rivede */ }
}
const touch = () => typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)')?.matches;

export default function ChatHistory({ handleTranslate, messageSearch, setMessageSearch, cercaAperta = false, setCercaAperta = () => {}, historyRef, pinned, setNewMessages, hasOlder, dispatch, selectedContact, messages, olderRequest, loading, fetching, activeSession, handleFetchFromWA, isGroup, setReplyTo, setForwardMsg, handleReact, onModifica = null }) {
  const [menu, setMenu] = useState(null);
  const [visore, setVisore] = useState(null);
  // Gli allegati della chat in ordine di tempo (l'elenco arriva dal più
  // recente), così nel visore si scorre avanti e indietro come nella chat.
  const apriAllegato = scelto => {
    const elementi = [...messages].reverse()
      .filter(m => m.id === scelto.id || apribile(m))
      .map(m => (m.id === scelto.id ? scelto : m));
    setVisore({ elementi, inizio: Math.max(0, elementi.findIndex(m => m.id === scelto.id)) });
  };
  const [suggerimento, setSuggerimento] = useState(() => !suggerimentoVisto());
  // Mentre si scorre, in alto compare il giorno dei messaggi in cima, poi
  // sparisce: come su WhatsApp. I separatori «agganciati» col CSS non vanno su
  // Safari in una chat che scorre dal basso (provato il 04/10/2026).
  const [giornoInAlto, setGiornoInAlto] = useState('');
  const giornoFrame = useRef(null);
  const giornoNascondi = useRef(null);
  const seguiGiorno = () => {
    const storia = historyRef.current;
    if (!storia || giornoFrame.current) return;
    giornoFrame.current = requestAnimationFrame(() => {
      giornoFrame.current = null;
      const cima = storia.getBoundingClientRect().top;
      let primo = null;
      for (const riga of storia.querySelectorAll('[data-quando]')) {
        const r = riga.getBoundingClientRect();
        if (r.bottom > cima && (!primo || r.top < primo.top)) primo = { top: r.top, quando: riga.dataset.quando };
      }
      setGiornoInAlto(primo ? etichettaGiorno(primo.quando) : '');
      clearTimeout(giornoNascondi.current);
      giornoNascondi.current = setTimeout(() => setGiornoInAlto(''), 1500);
    });
  };
  const gesto = useRef(null);
  const apri = msg => {
    setMenu(msg);
    if (suggerimento) { setSuggerimento(false); segnaSuggerimentoVisto(); }
  };
  const rispondi = msg => setReplyTo({ id: msg.id, waId: msg.whatsapp_message_id, body: msg.body?.substring(0, 50), author: msg.author_display || msg.author_name || msg.contact_name });
  const copia = msg => {
    Promise.resolve(navigator.clipboard?.writeText(msg.body || ''))
      .then(() => toast.success('Testo copiato'))
      .catch(() => toast.error('Copia non riuscita'));
  };
  // Dito sul fumetto: tenuto fermo apre il menu, trascinato a destra risponde.
  // Solo tocco e penna: col mouse ci sono il pulsante e il clic destro.
  const finisci = () => {
    const g = gesto.current;
    if (!g) return;
    clearTimeout(g.timer);
    if (g.el) g.el.style.transform = '';
    if (g.dx >= SCORRI_PER_RISPONDERE && activeSession) rispondi(g.msg);
    gesto.current = null;
  };
  const gesti = msg => ({
    onContextMenu: e => { e.preventDefault(); if (!gesto.current?.aperto) apri(msg); },
    onPointerDown: e => {
      if (e.pointerType === 'mouse') return;
      const g = { msg, x: e.clientX, y: e.clientY, dx: 0, el: e.currentTarget };
      g.timer = setTimeout(() => { g.aperto = true; navigator.vibrate?.(15); apri(msg); }, PRESSIONE_LUNGA_MS);
      gesto.current = g;
    },
    onPointerMove: e => {
      const g = gesto.current;
      if (!g || g.msg !== msg) return;
      const dx = e.clientX - g.x;
      const dy = e.clientY - g.y;
      if (Math.abs(dx) > 10 || Math.abs(dy) > 10) clearTimeout(g.timer);
      if (activeSession && dx > 0 && Math.abs(dy) < 30) {
        g.dx = Math.min(dx, 80);
        g.el.style.transform = `translateX(${g.dx}px)`;
      }
    },
    onPointerUp: finisci,
    onPointerCancel: finisci,
  });
  return (<>
{visore && <MediaViewer elementi={visore.elementi} inizio={visore.inizio} onClose={() => setVisore(null)}
  onForward={activeSession ? m => { setVisore(null); setForwardMsg(m.whatsapp_message_id); } : null} />}
{menu && <MessageMenu msg={menu} attivo={!!activeSession} onClose={() => setMenu(null)}
  onReply={() => rispondi(menu)} onForward={() => setForwardMsg(menu.whatsapp_message_id)}
  onReact={emoji => handleReact(menu.whatsapp_message_id, emoji)} onCopy={() => copia(menu)}
  onTranslate={handleTranslate ? () => handleTranslate(menu.id) : null}
  onEdit={onModifica ? () => onModifica(menu) : null} />}
{suggerimento && messages.length > 0 && (
  <p role="note" className="suggerimento-azioni mx-3 mt-2 flex items-center gap-2 rounded-lg bg-brand-50 px-3 py-2 text-xs text-ink">
    <span className="flex-1">{touch()
      ? 'Tieni premuto un messaggio per rispondere, reagire o inoltrare. Trascinalo a destra per rispondere.'
      : 'Passa sopra un messaggio e premi ⌄ (o usa il tasto destro) per rispondere, reagire o inoltrare.'}</span>
    <button type="button" aria-label="Chiudi il suggerimento" onClick={() => { setSuggerimento(false); segnaSuggerimentoVisto(); }}><MdClose size={16} /></button>
  </p>
)}
{/* La ricerca si apre dalla lente nell'intestazione: sempre aperta toglieva una riga alla chat. */}
{(cercaAperta || messageSearch) && <div className="chat-search-riga">
  <input className="chat-search" autoFocus aria-label="Cerca nei messaggi caricati" placeholder="Cerca nei messaggi caricati" value={messageSearch} onChange={e => setMessageSearch(e.target.value)} />
  <button type="button" className="icon-button" aria-label="Chiudi la ricerca" onClick={() => { setMessageSearch(''); setCercaAperta(false); }}><MdClose size={20} /></button>
</div>}
<div className="storia-contenitore">
{giornoInAlto && <div className="giorno-in-alto" aria-hidden="true"><span>{giornoInAlto}</span></div>}
            <div ref={historyRef} onScroll={() => { pinned.current = Math.abs(historyRef.current.scrollTop) < 80; if (pinned.current) setNewMessages(false); seguiGiorno(); }} className="chat-history flex-1 overflow-y-auto p-4 flex flex-col-reverse">
              {hasOlder && (
                <div className="flex justify-center pt-4 order-last">
                  <button onClick={() => dispatch(fetchOlderMessages({ contactId: selectedContact.id, before: messages[messages.length - 1].id }))} disabled={!!olderRequest}
                    className="text-xs px-4 py-2 text-info hover:bg-info-bg rounded-md disabled:opacity-50">
                    {olderRequest ? 'Caricamento...' : '↑ Carica messaggi precedenti salvati'}
                  </button>
                </div>
              )}

              {loading && messages.length === 0 ? (
                <p className="text-xs text-muted text-center py-8">Caricamento...</p>
              ) : messages.length === 0 && !fetching ? (
                <div className="text-center space-y-3 py-8">
                  <p className="text-sm text-muted">Nessun messaggio salvato</p>
                  {activeSession && (
                    <button onClick={() => handleFetchFromWA(50)} disabled={fetching}
                      className="text-sm px-5 py-2.5 bg-brand-50 text-brand-700 rounded-lg hover:bg-brand-50">
                      📥 Scarica messaggi da WhatsApp
                    </button>
                  )}
                </div>
              ) : fetching && messages.length === 0 ? (
                <p className="text-xs text-muted text-center py-8">⏳ Scaricando...</p>
              ) : messages.filter(msg => !messageSearch || `${msg.body || ''} ${msg.translation || ''}`.toLocaleLowerCase().includes(messageSearch.toLocaleLowerCase())).map((msg, i, elenco) => (
                <Fragment key={msg.id || i}>
                <div
                  data-quando={msg.timestamp}
                  className={`riga-messaggio flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}${continua(msg, elenco[i + 1]) ? ' continua' : ''} group`}
                  {...gesti(msg)}>
                  {/* Avatar for inbound in groups */}
                  {/* Come su WhatsApp la foto sta accanto all'ultimo fumetto di una serie; negli altri resta lo spazio. */}
                  {isGroup && msg.direction === 'inbound' && (
                    <div className="mr-2 mt-auto mb-1" style={{ minWidth: 24 }}>{!(i > 0 && continua(elenco[i - 1], msg)) && <Avatar contactId={selectedContact.id} author={msg.author} name={msg.author_display || msg.author_name || '?'} size={24} />}</div>
                  )}

                  <div className="chat-bubble relative max-w-[80%]">
                    <div className={`px-3 py-2 rounded-lg text-sm ${
                      msg.direction === 'outbound' ? 'bubble-out' : 'bubble-in'
                    }`}>
                      {/* Group sender name */}
                      {isGroup && msg.direction === 'inbound' && !continua(msg, elenco[i + 1]) && (msg.author_display || msg.author_name || msg.author) && (
                        <p className="text-xs font-semibold text-info mb-1">
                          {msg.author_display || msg.author_name || msg.author?.split('@')[0]}
                        </p>
                      )}
                      {/* Quoted message */}
                      <QuotedMessage msg={msg} />
                      {/* Content */}
                      {msg.contacts?.length ? <BigliettiContatto msg={msg} /> : !testuale(msg) ? <MediaMessage msg={msg} onOpen={apriAllegato} /> : (
                        <><TestoConLink testo={msg.body}>{oraNelTesto(msg) && !msg.translation && <SpazioOra msg={msg} />}</TestoConLink><AnteprimaLink messaggio={msg} /></>
                      )}
                      {/* Translation (only visible in frontend) */}
                      {msg.translation && (
                        <p className="text-xs text-muted mt-1 pt-1 border-t border-line whitespace-pre-wrap">
                          🌐 {msg.translation}{oraNelTesto(msg) && <SpazioOra msg={msg} />}
                        </p>
                      )}
                      <OraMessaggio msg={msg} nelTesto={oraNelTesto(msg)} />
                    </div>

                    {/* Toccare una reazione aggiunge la stessa. */}
                    <Reactions reactions={msg.reactions} onReact={activeSession ? emoji => handleReact(msg.whatsapp_message_id, emoji) : null} />

                    {/* Col mouse il pulsante compare passando sul fumetto, come in
                        WhatsApp Web. Sul telefono si tiene premuto il messaggio. */}
                    <button type="button" className="messaggio-azioni" aria-label="Azioni sul messaggio"
                      onClick={e => { e.stopPropagation(); apri(msg); }}>
                      <MdExpandMore size={20} aria-hidden="true" />
                    </button>
                  </div>
                </div>
                {/* La lista va dal più recente: il separatore viene dopo l'ultimo
                    messaggio di un giorno, e a schermo (colonna rovesciata) sopra. */}
                {chiaveGiorno(msg.timestamp) !== chiaveGiorno(elenco[i + 1]?.timestamp) && msg.timestamp && (
                  <div className="separatore-giorno" role="separator"><span>{etichettaGiorno(msg.timestamp)}</span></div>
                )}
                </Fragment>
              ))}
            </div>
</div>
</>); }

// L'ora sta nell'angolo in basso a destra del fumetto, sull'ultima riga del
// testo quando c'è posto, come su WhatsApp. Prima ogni fumetto aveva una riga
// in più con «03/10 21:12» (richiesta del 04/10/2026). Nel testo si lascia uno
// spazio invisibile largo quanto l'ora: se non entra va a capo, e il fumetto
// cresce di una riga.
// Come su WhatsApp: i messaggi di fila della stessa persona stanno quasi
// attaccati, quando cambia chi scrive c'è più aria (richiesta del 04/10/2026).
// `prima` è il messaggio sopra: la lista va dal più recente.
export const continua = (msg, prima) => !!prima && prima.direction === msg.direction
  && (msg.direction === 'outbound' || (prima.author || '') === (msg.author || ''))
  && chiaveGiorno(prima.timestamp) === chiaveGiorno(msg.timestamp);

const testuale = msg => !msg.message_type || msg.message_type === 'text';
const oraNelTesto = msg => testuale(msg) && !/https?:\/\//i.test(msg.body || '');
const SPUNTE = { pending: '◷', sent: '✓', delivered: '✓✓', read: '✓✓', failed: '!' };

function SpazioOra({ msg }) {
  return <span className={`spazio-ora${msg.direction === 'outbound' ? ' con-spunte' : ''}${msg.is_edited ? ' modificato' : ''}`} aria-hidden="true" />;
}

function OraMessaggio({ msg, nelTesto }) {
  return <span className={`ora-messaggio${nelTesto ? ' nel-testo' : ''}`}>
    {msg.is_edited && <em>modificato</em>}
    <time dateTime={msg.timestamp}>{msg.timestamp ? ora(msg.timestamp) : ''}</time>
    {msg.direction === 'outbound' && <span aria-label={msg.status} className={msg.status === 'read' ? 'spunte letto' : 'spunte'}>{SPUNTE[msg.status] || '◷'}</span>}
  </span>;
}
